import { getUserDisplayName, type User } from "../../hooks/useUser";
import { useClientTranslations } from "../../lib/i18n/client";
import { ProgressCircle } from "../home/ProgressCircle";
import styles from "./PersonalizedGreeting.module.css";

function getTimeOfDayGreetingKey(): string {
	const hour = new Date().getHours();
	if (hour < 12) return "greeting.goodMorning";
	if (hour < 18) return "greeting.goodAfternoon";
	return "greeting.goodEvening";
}

interface PersonalizedGreetingProps {
	user: User;
	showProgressCircle?: boolean;
	taskCount?: number;
	/** Real journey progress (0–100) for the ring. Falls back to 0 if omitted. */
	progressPercent?: number;
	completed?: boolean;
	subtitle?: string;
}

export function PersonalizedGreeting({
	user,
	showProgressCircle = false,
	taskCount = 3,
	progressPercent,
	completed = false,
	subtitle,
}: PersonalizedGreetingProps) {
	const { t } = useClientTranslations("common");

	const userName = getUserDisplayName(user);

	if (showProgressCircle) {
		// Real journey progress; `completed` (or reaching 100%) flips to the
		// finished-journey messaging. `completed` is authoritative over the ring,
		// and the value is clamped to 0–100 so the SVG math can't break.
		const isComplete = completed || (progressPercent ?? 0) >= 100;
		const percent = isComplete
			? 100
			: Math.min(100, Math.max(0, progressPercent ?? 0));
		return (
			<div className={styles.containerWithProgress}>
				<ProgressCircle percentage={percent} />
				<div className={styles.textBlock}>
					<h1 className={styles.titleStart}>
						{isComplete
							? t("home.completedTitle", { name: userName })
							: `${t(getTimeOfDayGreetingKey())}, ${userName}`}
					</h1>
					<p className={styles.subtitle}>
						{isComplete
							? t("home.completedSubtitle")
							: t("home.taskSubtitle", { count: taskCount })}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div
			className={`${styles.container}${subtitle ? ` ${styles.containerStart}` : ""}`}
		>
			<h1 className={styles.title}>
				{t(getTimeOfDayGreetingKey())}, {userName}
			</h1>
			{subtitle && <p className={styles.subtitle}>{subtitle}</p>}
		</div>
	);
}
